import React from 'react'
import { ERROR_BANNER_SCROLL_THRESHOLD } from '../../Helper/EnhancedData/Constants'
import { sortLineItemRowErrorMap } from '../../Helper/EnhancedData/EnhancedDataUtils'
import { EnhancedDataBackendError, EnhancedDataValidationError, LineItemFieldErrorMap } from '../../Type/EnhancedData'
import { GreenSolidExclamationIcon, RedSolidExclamationIcon } from '../Icons'

export const SuccessFeedback = ({ message }: {message: string}): React.JSX.Element => {
  return (
    <div className="success feedback">
      <span style={{ paddingRight: '5px' }}>
        <GreenSolidExclamationIcon/>
      </span>
      <span>
        {message}
      </span>
    </div>
  )
}

export const EDTotalsFeedback = ({ content }: {content: React.JSX.Element}): React.JSX.Element => {
  return (
    <div className={'error'} style={{ margin: '10px 0px' }}>
      <span style={{ paddingRight: '5px' }}>
        <RedSolidExclamationIcon/>
      </span>
      <span>
        {content}
      </span>
    </div>
  )
}

const buildLineItemFieldErrorComponent = (
  index: Number,
  lineItemFieldErrorMap: LineItemFieldErrorMap
): React.JSX.Element => {
  return (
    <>
      <span className={'line-item-error-heading'}>
        {`Line ${index}:`}
      </span>
      <ul className={'line-item-error-list'}>
        {
          Object.entries(lineItemFieldErrorMap).map(([fieldName, fieldError]) => {
            return fieldError.displayName
              ? (<li key={fieldName}> {fieldError.displayName}: {fieldError.message}</li>)
              : (<li key={fieldName}> {fieldError.message}</li>)
          })
        }
      </ul>
    </>
  )
}

export const BackendErrorFeedback = ({ otherErrors = [], lineItemErrors = {}, fieldErrorMap = {} }: EnhancedDataBackendError): React.JSX.Element => {
  const shouldEnableScroll = (): boolean => {
    const otherFieldErrorCount = Object.keys(otherErrors).length
    const edFieldErrorCount = Object.keys(fieldErrorMap).length
    const lineItemErrorCount = Object.values(lineItemErrors).reduce(
      (sum, rowError) => sum + Object.keys(rowError).length, 0
    )
    const totalErrorCount = otherFieldErrorCount + edFieldErrorCount + lineItemErrorCount
    return totalErrorCount >= ERROR_BANNER_SCROLL_THRESHOLD
  }
  const scrollStyle: React.CSSProperties = shouldEnableScroll() ? { maxHeight: '250px', overflowY: 'auto' } : {}
  return (
    <div className={'error'} style={scrollStyle}>
      <span style={{ paddingRight: '5px' }}>
        <RedSolidExclamationIcon/>
      </span>
      <span className={'line-item-error-heading'}>
        {'Please correct the following errors:'}
      </span>
      {
        fieldErrorMap &&
        <ul>
          {
            Object.entries(fieldErrorMap).map(
              ([fieldName, fieldError]) => <li key={fieldName}> {fieldError.displayName}: {fieldError.message} </li>,
            )
          }
        </ul>
      }
      <ul>
        {otherErrors.map((errorMessage, index) => <li key={index}> {errorMessage} </li>)}
      </ul>
      {
        Object.entries(lineItemErrors).map(
          ([key, lineItemFieldErrorMap]) => buildLineItemFieldErrorComponent(
            Number(key) + 1, lineItemFieldErrorMap,
          ),
        )
      }
    </div>
  )
}

export const ValidationErrorFeedback = ({ inputErrors = {}, lineItemErrors = {} }: EnhancedDataValidationError): React.JSX.Element => {
  const sortedRowErrorMap = sortLineItemRowErrorMap(lineItemErrors)
  const shouldEnableScroll = (): boolean => {
    const inputErrorCount = Object.keys(inputErrors).length
    const lineItemErrorCount = Object.values(lineItemErrors).reduce(
      (sum, rowError) => sum + Object.keys(rowError.fieldErrorMap).length, 0
    )
    const totalErrorCount = inputErrorCount + lineItemErrorCount
    return totalErrorCount >= ERROR_BANNER_SCROLL_THRESHOLD
  }

  const scrollStyle: React.CSSProperties = shouldEnableScroll() ? { maxHeight: '250px', overflowY: 'auto' } : {}

  return (
    <div className={'error'} style={scrollStyle}>
      <span style={{ paddingRight: '5px' }}>
        <RedSolidExclamationIcon />
      </span>
      <span className={'line-item-error-heading'}>
        {'Please correct the following errors:'}
      </span>
      <ul>
        {
          Object.entries(inputErrors).map(([_, errMsg], index) => (<li key={index}> {errMsg} </li>))
        }
      </ul>
      {
        Object.entries(sortedRowErrorMap).map(([_, lineItemError]) => {
          const index = lineItemError.index
          const fieldErrorMap = lineItemError.fieldErrorMap
          return buildLineItemFieldErrorComponent(index + 1, fieldErrorMap)
        })
      }
    </div>
  )
}
